/**
 * Phone validation utilities for different countries
 */

// Phone number patterns for different countries
const PHONE_PATTERNS = {
  'CA': /^\+1-\d{3}-\d{3}-\d{4}$/, // +1-XXX-XXX-XXXX
  'US': /^\+1-\d{3}-\d{3}-\d{4}$/, // +1-XXX-XXX-XXXX
  'FR': /^\+33-0\d-\d{2}-\d{2}-\d{2}-\d{2}$/, // +33-0X-XX-XX-XX-XX
};

// Phone format examples for display
export const PHONE_FORMAT_EXAMPLES = {
  'CA': '+1-xxx-xxx-xxxx',
  'US': '+1-xxx-xxx-xxxx',
  'FR': '+33-0x-xx-xx-xx-xx',
  'default': '+1-xxx-xxx-xxxx'
};

/**
 * Validate phone number based on country
 * @param {string} phone - The phone number to validate
 * @param {string} country - The country code (e.g., 'CA', 'US', 'FR')
 * @returns {boolean} - Whether the phone number is valid
 */
export const validatePhoneForCountry = (phone, country) => {
  if (!phone) return true; // Allow empty phone numbers
  
  const pattern = PHONE_PATTERNS[country] || PHONE_PATTERNS['CA']; // Default to Canada/US format
  return pattern.test(phone);
};

/**
 * Format phone number based on country
 * @param {string} phone - The phone number to format
 * @param {string} country - The country code
 * @returns {string} - Formatted phone number
 */
export const formatPhoneForCountry = (phone, country = 'CA') => {
  if (!phone) return '';
  
  // Remove all non-numeric characters
  const cleaned = phone.replace(/\D/g, '');
  
  switch (country) {
    case 'CA':
    case 'US':
      // Format as +1-XXX-XXX-XXXX
      if (cleaned.length === 10) {
        return `+1-${cleaned.substring(0, 3)}-${cleaned.substring(3, 6)}-${cleaned.substring(6, 10)}`;
      } else if (cleaned.length === 11 && cleaned.startsWith('1')) {
        // Remove leading 1 for North America
        const withoutCountryCode = cleaned.substring(1);
        return `+1-${withoutCountryCode.substring(0, 3)}-${withoutCountryCode.substring(3, 6)}-${withoutCountryCode.substring(6, 10)}`;
      }
      break;
    case 'FR':
      // Format as +33-0X-XX-XX-XX-XX
      if (cleaned.length === 10 && cleaned.startsWith('0')) {
        return `+33-${cleaned.substring(0, 2)}-${cleaned.substring(2, 4)}-${cleaned.substring(4, 6)}-${cleaned.substring(6, 8)}-${cleaned.substring(8, 10)}`;
      }
      break;
    default:
      // Default to North American format
      if (cleaned.length === 10) {
        return `+1-${cleaned.substring(0, 3)}-${cleaned.substring(3, 6)}-${cleaned.substring(6, 10)}`;
      }
  }
  
  return phone; // Return original if can't format
};

/**
 * Get phone format example for a country
 * @param {string} country - The country code
 * @returns {string} - Phone format example
 */
export const getPhoneFormatExample = (country) => {
  return PHONE_FORMAT_EXAMPLES[country] || PHONE_FORMAT_EXAMPLES['default'];
};

/**
 * Countries list with Canada and US prioritized
 */
export const COUNTRIES = [
  { code: 'CA', name: 'Canada', priority: 1 },
  { code: 'US', name: 'United States', priority: 2 },
  { code: '---', name: '---', priority: 3, disabled: true }, // Separator
  { code: 'AD', name: 'Andorra', priority: 4 },
  { code: 'AE', name: 'United Arab Emirates', priority: 4 },
  { code: 'AF', name: 'Afghanistan', priority: 4 },
  { code: 'AG', name: 'Antigua and Barbuda', priority: 4 },
  { code: 'AI', name: 'Anguilla', priority: 4 },
  { code: 'AL', name: 'Albania', priority: 4 },
  { code: 'AM', name: 'Armenia', priority: 4 },
  { code: 'AO', name: 'Angola', priority: 4 },
  { code: 'AQ', name: 'Antarctica', priority: 4 },
  { code: 'AR', name: 'Argentina', priority: 4 },
  { code: 'AS', name: 'American Samoa', priority: 4 },
  { code: 'AT', name: 'Austria', priority: 4 },
  { code: 'AU', name: 'Australia', priority: 4 },
  { code: 'AW', name: 'Aruba', priority: 4 },
  { code: 'AX', name: 'Åland Islands', priority: 4 },
  { code: 'AZ', name: 'Azerbaijan', priority: 4 },
  { code: 'BA', name: 'Bosnia and Herzegovina', priority: 4 },
  { code: 'BB', name: 'Barbados', priority: 4 },
  { code: 'BD', name: 'Bangladesh', priority: 4 },
  { code: 'BE', name: 'Belgium', priority: 4 },
  { code: 'BF', name: 'Burkina Faso', priority: 4 },
  { code: 'BG', name: 'Bulgaria', priority: 4 },
  { code: 'BH', name: 'Bahrain', priority: 4 },
  { code: 'BI', name: 'Burundi', priority: 4 },
  { code: 'BJ', name: 'Benin', priority: 4 },
  { code: 'BL', name: 'Saint Barthélemy', priority: 4 },
  { code: 'BM', name: 'Bermuda', priority: 4 },
  { code: 'BN', name: 'Brunei Darussalam', priority: 4 },
  { code: 'BO', name: 'Bolivia', priority: 4 },
  { code: 'BQ', name: 'Caribbean Netherlands', priority: 4 },
  { code: 'BR', name: 'Brazil', priority: 4 },
  { code: 'BS', name: 'Bahamas', priority: 4 },
  { code: 'BT', name: 'Bhutan', priority: 4 },
  { code: 'BV', name: 'Bouvet Island', priority: 4 },
  { code: 'BW', name: 'Botswana', priority: 4 },
  { code: 'BY', name: 'Belarus', priority: 4 },
  { code: 'BZ', name: 'Belize', priority: 4 },
  { code: 'CC', name: 'Cocos Islands', priority: 4 },
  { code: 'CD', name: 'Congo (Democratic Republic)', priority: 4 },
  { code: 'CF', name: 'Central African Republic', priority: 4 },
  { code: 'CG', name: 'Congo', priority: 4 },
  { code: 'CH', name: 'Switzerland', priority: 4 },
  { code: 'CI', name: 'Côte d\'Ivoire', priority: 4 },
  { code: 'CK', name: 'Cook Islands', priority: 4 },
  { code: 'CL', name: 'Chile', priority: 4 },
  { code: 'CM', name: 'Cameroon', priority: 4 },
  { code: 'CN', name: 'China', priority: 4 },
  { code: 'CO', name: 'Colombia', priority: 4 },
  { code: 'CR', name: 'Costa Rica', priority: 4 },
  { code: 'CU', name: 'Cuba', priority: 4 },
  { code: 'CV', name: 'Cape Verde', priority: 4 },
  { code: 'CW', name: 'Curaçao', priority: 4 },
  { code: 'CX', name: 'Christmas Island', priority: 4 },
  { code: 'CY', name: 'Cyprus', priority: 4 },
  { code: 'CZ', name: 'Czech Republic', priority: 4 },
  { code: 'DE', name: 'Germany', priority: 4 },
  { code: 'DJ', name: 'Djibouti', priority: 4 },
  { code: 'DK', name: 'Denmark', priority: 4 },
  { code: 'DM', name: 'Dominica', priority: 4 },
  { code: 'DO', name: 'Dominican Republic', priority: 4 },
  { code: 'DZ', name: 'Algeria', priority: 4 },
  { code: 'EC', name: 'Ecuador', priority: 4 },
  { code: 'EE', name: 'Estonia', priority: 4 },
  { code: 'EG', name: 'Egypt', priority: 4 },
  { code: 'EH', name: 'Western Sahara', priority: 4 },
  { code: 'ER', name: 'Eritrea', priority: 4 },
  { code: 'ES', name: 'Spain', priority: 4 },
  { code: 'ET', name: 'Ethiopia', priority: 4 },
  { code: 'FI', name: 'Finland', priority: 4 },
  { code: 'FJ', name: 'Fiji', priority: 4 },
  { code: 'FK', name: 'Falkland Islands', priority: 4 },
  { code: 'FM', name: 'Micronesia', priority: 4 },
  { code: 'FO', name: 'Faroe Islands', priority: 4 },
  { code: 'FR', name: 'France', priority: 4 },
  { code: 'GA', name: 'Gabon', priority: 4 },
  { code: 'GB', name: 'United Kingdom', priority: 4 },
  { code: 'GD', name: 'Grenada', priority: 4 },
  { code: 'GE', name: 'Georgia', priority: 4 },
  { code: 'GF', name: 'French Guiana', priority: 4 },
  { code: 'GG', name: 'Guernsey', priority: 4 },
  { code: 'GH', name: 'Ghana', priority: 4 },
  { code: 'GI', name: 'Gibraltar', priority: 4 },
  { code: 'GL', name: 'Greenland', priority: 4 },
  { code: 'GM', name: 'Gambia', priority: 4 },
  { code: 'GN', name: 'Guinea', priority: 4 },
  { code: 'GP', name: 'Guadeloupe', priority: 4 },
  { code: 'GQ', name: 'Equatorial Guinea', priority: 4 },
  { code: 'GR', name: 'Greece', priority: 4 },
  { code: 'GS', name: 'South Georgia', priority: 4 },
  { code: 'GT', name: 'Guatemala', priority: 4 },
  { code: 'GU', name: 'Guam', priority: 4 },
  { code: 'GW', name: 'Guinea-Bissau', priority: 4 },
  { code: 'GY', name: 'Guyana', priority: 4 },
  { code: 'HK', name: 'Hong Kong', priority: 4 },
  { code: 'HM', name: 'Heard Island', priority: 4 },
  { code: 'HN', name: 'Honduras', priority: 4 },
  { code: 'HR', name: 'Croatia', priority: 4 },
  { code: 'HT', name: 'Haiti', priority: 4 },
  { code: 'HU', name: 'Hungary', priority: 4 },
  { code: 'ID', name: 'Indonesia', priority: 4 },
  { code: 'IE', name: 'Ireland', priority: 4 },
  { code: 'IL', name: 'Israel', priority: 4 },
  { code: 'IM', name: 'Isle of Man', priority: 4 },
  { code: 'IN', name: 'India', priority: 4 },
  { code: 'IO', name: 'British Indian Ocean Territory', priority: 4 },
  { code: 'IQ', name: 'Iraq', priority: 4 },
  { code: 'IR', name: 'Iran', priority: 4 },
  { code: 'IS', name: 'Iceland', priority: 4 },
  { code: 'IT', name: 'Italy', priority: 4 },
  { code: 'JE', name: 'Jersey', priority: 4 },
  { code: 'JM', name: 'Jamaica', priority: 4 },
  { code: 'JO', name: 'Jordan', priority: 4 },
  { code: 'JP', name: 'Japan', priority: 4 },
  { code: 'KE', name: 'Kenya', priority: 4 },
  { code: 'KG', name: 'Kyrgyzstan', priority: 4 },
  { code: 'KH', name: 'Cambodia', priority: 4 },
  { code: 'KI', name: 'Kiribati', priority: 4 },
  { code: 'KM', name: 'Comoros', priority: 4 },
  { code: 'KN', name: 'Saint Kitts and Nevis', priority: 4 },
  { code: 'KP', name: 'North Korea', priority: 4 },
  { code: 'KR', name: 'South Korea', priority: 4 },
  { code: 'KW', name: 'Kuwait', priority: 4 },
  { code: 'KY', name: 'Cayman Islands', priority: 4 },
  { code: 'KZ', name: 'Kazakhstan', priority: 4 },
  { code: 'LA', name: 'Laos', priority: 4 },
  { code: 'LB', name: 'Lebanon', priority: 4 },
  { code: 'LC', name: 'Saint Lucia', priority: 4 },
  { code: 'LI', name: 'Liechtenstein', priority: 4 },
  { code: 'LK', name: 'Sri Lanka', priority: 4 },
  { code: 'LR', name: 'Liberia', priority: 4 },
  { code: 'LS', name: 'Lesotho', priority: 4 },
  { code: 'LT', name: 'Lithuania', priority: 4 },
  { code: 'LU', name: 'Luxembourg', priority: 4 },
  { code: 'LV', name: 'Latvia', priority: 4 },
  { code: 'LY', name: 'Libya', priority: 4 },
  { code: 'MA', name: 'Morocco', priority: 4 },
  { code: 'MC', name: 'Monaco', priority: 4 },
  { code: 'MD', name: 'Moldova', priority: 4 },
  { code: 'ME', name: 'Montenegro', priority: 4 },
  { code: 'MF', name: 'Saint Martin', priority: 4 },
  { code: 'MG', name: 'Madagascar', priority: 4 },
  { code: 'MH', name: 'Marshall Islands', priority: 4 },
  { code: 'MK', name: 'North Macedonia', priority: 4 },
  { code: 'ML', name: 'Mali', priority: 4 },
  { code: 'MM', name: 'Myanmar', priority: 4 },
  { code: 'MN', name: 'Mongolia', priority: 4 },
  { code: 'MO', name: 'Macao', priority: 4 },
  { code: 'MP', name: 'Northern Mariana Islands', priority: 4 },
  { code: 'MQ', name: 'Martinique', priority: 4 },
  { code: 'MR', name: 'Mauritania', priority: 4 },
  { code: 'MS', name: 'Montserrat', priority: 4 },
  { code: 'MT', name: 'Malta', priority: 4 },
  { code: 'MU', name: 'Mauritius', priority: 4 },
  { code: 'MV', name: 'Maldives', priority: 4 },
  { code: 'MW', name: 'Malawi', priority: 4 },
  { code: 'MX', name: 'Mexico', priority: 4 },
  { code: 'MY', name: 'Malaysia', priority: 4 },
  { code: 'MZ', name: 'Mozambique', priority: 4 },
  { code: 'NA', name: 'Namibia', priority: 4 },
  { code: 'NC', name: 'New Caledonia', priority: 4 },
  { code: 'NE', name: 'Niger', priority: 4 },
  { code: 'NF', name: 'Norfolk Island', priority: 4 },
  { code: 'NG', name: 'Nigeria', priority: 4 },
  { code: 'NI', name: 'Nicaragua', priority: 4 },
  { code: 'NL', name: 'Netherlands', priority: 4 },
  { code: 'NO', name: 'Norway', priority: 4 },
  { code: 'NP', name: 'Nepal', priority: 4 },
  { code: 'NR', name: 'Nauru', priority: 4 },
  { code: 'NU', name: 'Niue', priority: 4 },
  { code: 'NZ', name: 'New Zealand', priority: 4 },
  { code: 'OM', name: 'Oman', priority: 4 },
  { code: 'PA', name: 'Panama', priority: 4 },
  { code: 'PE', name: 'Peru', priority: 4 },
  { code: 'PF', name: 'French Polynesia', priority: 4 },
  { code: 'PG', name: 'Papua New Guinea', priority: 4 },
  { code: 'PH', name: 'Philippines', priority: 4 },
  { code: 'PK', name: 'Pakistan', priority: 4 },
  { code: 'PL', name: 'Poland', priority: 4 },
  { code: 'PM', name: 'Saint Pierre and Miquelon', priority: 4 },
  { code: 'PN', name: 'Pitcairn', priority: 4 },
  { code: 'PR', name: 'Puerto Rico', priority: 4 },
  { code: 'PS', name: 'Palestine', priority: 4 },
  { code: 'PT', name: 'Portugal', priority: 4 },
  { code: 'PW', name: 'Palau', priority: 4 },
  { code: 'PY', name: 'Paraguay', priority: 4 },
  { code: 'QA', name: 'Qatar', priority: 4 },
  { code: 'RE', name: 'Réunion', priority: 4 },
  { code: 'RO', name: 'Romania', priority: 4 },
  { code: 'RS', name: 'Serbia', priority: 4 },
  { code: 'RU', name: 'Russia', priority: 4 },
  { code: 'RW', name: 'Rwanda', priority: 4 },
  { code: 'SA', name: 'Saudi Arabia', priority: 4 },
  { code: 'SB', name: 'Solomon Islands', priority: 4 },
  { code: 'SC', name: 'Seychelles', priority: 4 },
  { code: 'SD', name: 'Sudan', priority: 4 },
  { code: 'SE', name: 'Sweden', priority: 4 },
  { code: 'SG', name: 'Singapore', priority: 4 },
  { code: 'SH', name: 'Saint Helena', priority: 4 },
  { code: 'SI', name: 'Slovenia', priority: 4 },
  { code: 'SJ', name: 'Svalbard and Jan Mayen', priority: 4 },
  { code: 'SK', name: 'Slovakia', priority: 4 },
  { code: 'SL', name: 'Sierra Leone', priority: 4 },
  { code: 'SM', name: 'San Marino', priority: 4 },
  { code: 'SN', name: 'Senegal', priority: 4 },
  { code: 'SO', name: 'Somalia', priority: 4 },
  { code: 'SR', name: 'Suriname', priority: 4 },
  { code: 'SS', name: 'South Sudan', priority: 4 },
  { code: 'ST', name: 'São Tomé and Príncipe', priority: 4 },
  { code: 'SV', name: 'El Salvador', priority: 4 },
  { code: 'SX', name: 'Sint Maarten', priority: 4 },
  { code: 'SY', name: 'Syria', priority: 4 },
  { code: 'SZ', name: 'Eswatini', priority: 4 },
  { code: 'TC', name: 'Turks and Caicos Islands', priority: 4 },
  { code: 'TD', name: 'Chad', priority: 4 },
  { code: 'TF', name: 'French Southern Territories', priority: 4 },
  { code: 'TG', name: 'Togo', priority: 4 },
  { code: 'TH', name: 'Thailand', priority: 4 },
  { code: 'TJ', name: 'Tajikistan', priority: 4 },
  { code: 'TK', name: 'Tokelau', priority: 4 },
  { code: 'TL', name: 'Timor-Leste', priority: 4 },
  { code: 'TM', name: 'Turkmenistan', priority: 4 },
  { code: 'TN', name: 'Tunisia', priority: 4 },
  { code: 'TO', name: 'Tonga', priority: 4 },
  { code: 'TR', name: 'Turkey', priority: 4 },
  { code: 'TT', name: 'Trinidad and Tobago', priority: 4 },
  { code: 'TV', name: 'Tuvalu', priority: 4 },
  { code: 'TW', name: 'Taiwan', priority: 4 },
  { code: 'TZ', name: 'Tanzania', priority: 4 },
  { code: 'UA', name: 'Ukraine', priority: 4 },
  { code: 'UG', name: 'Uganda', priority: 4 },
  { code: 'UM', name: 'United States Minor Outlying Islands', priority: 4 },
  { code: 'UY', name: 'Uruguay', priority: 4 },
  { code: 'UZ', name: 'Uzbekistan', priority: 4 },
  { code: 'VA', name: 'Vatican City', priority: 4 },
  { code: 'VC', name: 'Saint Vincent and the Grenadines', priority: 4 },
  { code: 'VE', name: 'Venezuela', priority: 4 },
  { code: 'VG', name: 'British Virgin Islands', priority: 4 },
  { code: 'VI', name: 'US Virgin Islands', priority: 4 },
  { code: 'VN', name: 'Vietnam', priority: 4 },
  { code: 'VU', name: 'Vanuatu', priority: 4 },
  { code: 'WF', name: 'Wallis and Futuna', priority: 4 },
  { code: 'WS', name: 'Samoa', priority: 4 },
  { code: 'YE', name: 'Yemen', priority: 4 },
  { code: 'YT', name: 'Mayotte', priority: 4 },
  { code: 'ZA', name: 'South Africa', priority: 4 },
  { code: 'ZM', name: 'Zambia', priority: 4 },
  { code: 'ZW', name: 'Zimbabwe', priority: 4 }
].sort((a, b) => a.priority - b.priority || a.name.localeCompare(b.name));

/**
 * Canadian provinces and territories
 */
export const CANADIAN_PROVINCES = [
  { code: 'AB', name: 'Alberta' },
  { code: 'BC', name: 'British Columbia' },
  { code: 'MB', name: 'Manitoba' },
  { code: 'NB', name: 'New Brunswick' },
  { code: 'NL', name: 'Newfoundland and Labrador' },
  { code: 'NS', name: 'Nova Scotia' },
  { code: 'ON', name: 'Ontario' },
  { code: 'PE', name: 'Prince Edward Island' },
  { code: 'QC', name: 'Quebec' },
  { code: 'SK', name: 'Saskatchewan' },
  { code: 'NT', name: 'Northwest Territories' },
  { code: 'NU', name: 'Nunavut' },
  { code: 'YT', name: 'Yukon' }
];

/**
 * US states
 */
export const US_STATES = [
  { code: 'AL', name: 'Alabama' },
  { code: 'AK', name: 'Alaska' },
  { code: 'AZ', name: 'Arizona' },
  { code: 'AR', name: 'Arkansas' },
  { code: 'CA', name: 'California' },
  { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' },
  { code: 'DE', name: 'Delaware' },
  { code: 'FL', name: 'Florida' },
  { code: 'GA', name: 'Georgia' },
  { code: 'HI', name: 'Hawaii' },
  { code: 'ID', name: 'Idaho' },
  { code: 'IL', name: 'Illinois' },
  { code: 'IN', name: 'Indiana' },
  { code: 'IA', name: 'Iowa' },
  { code: 'KS', name: 'Kansas' },
  { code: 'KY', name: 'Kentucky' },
  { code: 'LA', name: 'Louisiana' },
  { code: 'ME', name: 'Maine' },
  { code: 'MD', name: 'Maryland' },
  { code: 'MA', name: 'Massachusetts' },
  { code: 'MI', name: 'Michigan' },
  { code: 'MN', name: 'Minnesota' },
  { code: 'MS', name: 'Mississippi' },
  { code: 'MO', name: 'Missouri' },
  { code: 'MT', name: 'Montana' },
  { code: 'NE', name: 'Nebraska' },
  { code: 'NV', name: 'Nevada' },
  { code: 'NH', name: 'New Hampshire' },
  { code: 'NJ', name: 'New Jersey' },
  { code: 'NM', name: 'New Mexico' },
  { code: 'NY', name: 'New York' },
  { code: 'NC', name: 'North Carolina' },
  { code: 'ND', name: 'North Dakota' },
  { code: 'OH', name: 'Ohio' },
  { code: 'OK', name: 'Oklahoma' },
  { code: 'OR', name: 'Oregon' },
  { code: 'PA', name: 'Pennsylvania' },
  { code: 'RI', name: 'Rhode Island' },
  { code: 'SC', name: 'South Carolina' },
  { code: 'SD', name: 'South Dakota' },
  { code: 'TN', name: 'Tennessee' },
  { code: 'TX', name: 'Texas' },
  { code: 'UT', name: 'Utah' },
  { code: 'VT', name: 'Vermont' },
  { code: 'VA', name: 'Virginia' },
  { code: 'WA', name: 'Washington' },
  { code: 'WV', name: 'West Virginia' },
  { code: 'WI', name: 'Wisconsin' },
  { code: 'WY', name: 'Wyoming' },
  { code: 'DC', name: 'District of Columbia' }
];

/**
 * Get states/provinces for a country
 * @param {string} countryCode - The country code
 * @returns {Array} - Array of states/provinces
 */
export const getStatesProvinces = (countryCode) => {
  switch (countryCode) {
    case 'CA':
      return CANADIAN_PROVINCES;
    case 'US':
      return US_STATES;
    default:
      return [];
  }
};

/**
 * Get the label for state/province field based on country
 * @param {string} countryCode - The country code
 * @returns {string} - The appropriate label
 */
export const getStateProvinceLabel = (countryCode) => {
  switch (countryCode) {
    case 'CA':
      return 'Province';
    case 'US':
      return 'State';
    default:
      return 'State/Province';
  }
}; 